import NextHead from "next/head";
import NextLink from "next/link";

import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import Form from "./form";
import { useAppSelector } from "@/redux/store/hooks";
import {
  Text,
  Heading,
  Alert,
  CloseButton,
  Flex,
  Button
} from "@/storybook-components";
import SocialSignIn from "./social-sign-in";
import SingleSignOn from "./single-sign-on";
import isRelativePath from "@/helpers/is-relative-path";
import { parseNextUrlFromQuery } from "@/helpers/parse-next-url";

const App = (): JSX.Element => {
  const router = useRouter();
  const { translate } = useTranslator();
  const { name } = useAppSelector((state) => state.site.site);
  const { disableSignup } = useAppSelector((state) => state.settings.settings);
  const { isLoggedIn } = useAppSelector((state) => state.user.registeredUser);
  const { features } = useAppSelector((state) => state.features);

  const isGranicusSSOEnabled = features?.granicusSso?.enabled;

  const [isPrivateRoute, setIsPrivateRoute] = useState(
    Boolean(router.query["private"]) === true
  );
  const [isPasswordReset, setIsPasswordReset] = useState(
    !!router.query["redirectionStatus"]
  );
  const [isSignUpDisabled, setIsSignUpDisabled] = useState(
    !!router.query["signupdisabled"]
  );

  // Parse and decode the next URL parameter
  const nextPageUrl = parseNextUrlFromQuery(router.query);

  // Redirect user after login
  useEffect(() => {
    if (isLoggedIn && process.env.NODE_ENV === "development") {
      if (nextPageUrl && isRelativePath(nextPageUrl)) router.push(nextPageUrl);
      else router.push("/");
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isSignUpDisabled) {
      router.replace("/login", undefined, { shallow: true });
    }
  }, []);

  return (
    <>
      <NextHead>
        <title>{`${translate("login.signIn")} | ${name}`}</title>
        <meta name="description" content={translate("login.description")} />
      </NextHead>
      {isPrivateRoute && (
        <Alert.Root
          status="info"
          borderRadius="md"
          data-testid="login-participant-alert-private-route"
        >
          <Alert.Indicator />
          <Alert.Title>{translate("login.thisPageIsPrivate")}</Alert.Title>
          <CloseButton
            ml="auto"
            onClick={() => setIsPrivateRoute(false)}
            data-testid="login-participant-alert-private-route-close"
          />
        </Alert.Root>
      )}
      {router.query["redirectionStatus"] && isPasswordReset && (
        <Alert.Root
          status="success"
          borderRadius="md"
          alignItems="flex-start"
          data-testid="login-participant-alert-password-reset"
        >
          <Alert.Indicator />
          <Alert.Title>{translate("login.passwordChanged")}</Alert.Title>
          <CloseButton
            ml="auto"
            onClick={() => setIsPasswordReset(false)}
            data-testid="login-participant-alert-password-reset-close"
          />
        </Alert.Root>
      )}
      {isSignUpDisabled && (
        <Alert.Root
          status="error"
          borderRadius="md"
          alignItems="flex-start"
          data-testid="login-participant-alert-signup-disabled"
        >
          <Alert.Indicator />
          <Flex flexDirection="column">
            <Alert.Title>{translate("login.signUpDisabledTitle")}</Alert.Title>
            <Alert.Description>
              {translate("login.signUpDisabledDiscription")}
            </Alert.Description>
          </Flex>
          <CloseButton
            ml="auto"
            onClick={() => setIsSignUpDisabled(false)}
            data-testid="login-participant-alert-signup-disabled-close"
          />
        </Alert.Root>
      )}
      <Heading as="h1" my={3} data-testid="login-participant-heading">
        {translate("login.signIn")}
      </Heading>
      <Form />

      {isGranicusSSOEnabled ? (
        <NextLink href="/auth/sso">
          <Button
            variant="secondary"
            my={3}
            size="lg"
            width="full"
            data-testid="login-participant-button-staff-sso"
          >
            {translate("login.staffSignIn")}
          </Button>
        </NextLink>
      ) : (
        <SingleSignOn />
      )}

      {!disableSignup && (
        <>
          <SocialSignIn />
          <Text mt={4}>
            {translate("login.getInvolvedInConversations")}&nbsp;
            <NextLink
              href={
                nextPageUrl
                  ? `/register?next=${encodeURIComponent(nextPageUrl)}`
                  : "/register"
              }
              passHref
              data-testid="login-participant-link-create-account"
            >
              {translate("login.createAnAccount")}
            </NextLink>{" "}
            {translate("common.today")}
          </Text>
        </>
      )}
    </>
  );
};

export default App;
